// Editable firm details — update these as the entity is confirmed.
export const FIRM = {
  name: "AMADI Legal Consultancy",
  entity: "AMADI Legal Consultancy",
  governingLaw: "the United Arab Emirates",
  email: "hello@amadi-legal.example",
};

const UPDATED = "July 2026";

// Footer / sitemap navigation order (label + route).
export const LEGAL_NAV = [
  { label: "Terms & Conditions", to: "/legal/terms" },
  { label: "Privacy Notice", to: "/legal/privacy" },
  { label: "Legal Notices", to: "/legal/legal-notices" },
  { label: "Cookies Notice", to: "/legal/cookies" },
  { label: "Fraud Alert", to: "/legal/fraud-alert" },
  { label: "Trademark Policy", to: "/legal/trademark" },
  { label: "Sitemap", to: "/sitemap" },
];

export const LEGAL_DOCS = {
  terms: {
    title: "Terms & Conditions",
    description:
      "The terms governing your use of the AMADI Legal Consultancy website.",
    updated: UPDATED,
    body: `## 1. Acceptance of these terms

These Terms & Conditions ("Terms") govern your access to and use of the ${FIRM.name} website (the "Website"). By accessing or using the Website, you agree to be bound by these Terms. If you do not agree, please do not use the Website.

## 2. Use of the Website

You may use the Website for lawful, informational purposes only. You agree not to use the Website in any way that could damage, disable, or impair it, or interfere with any other party's use.

## 3. No legal advice

The content on this Website is provided for general information only and does not constitute legal advice. Accessing this Website, or contacting us through it, does not create a lawyer–client relationship. You should not act, or refrain from acting, on the basis of any content here without obtaining formal advice from qualified counsel on your specific circumstances.

## 4. Intellectual property

All content on the Website — including text, graphics, logos, and design — is owned by or licensed to ${FIRM.entity} and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.

## 5. Third-party links

The Website may contain links to third-party websites. We are not responsible for the content, policies, or practices of any third-party site and provide such links for convenience only.

## 6. Disclaimers and limitation of liability

The Website is provided on an "as is" and "as available" basis without warranties of any kind. To the fullest extent permitted by law, ${FIRM.entity} shall not be liable for any loss or damage arising from your use of, or reliance on, the Website or its content.

## 7. Governing law

These Terms are governed by the laws of ${FIRM.governingLaw}, and any disputes shall be subject to the exclusive jurisdiction of its competent courts.

## 8. Changes to these terms

We may update these Terms from time to time. The version published on the Website at the time of your use applies.

## 9. Contact

Questions about these Terms may be directed to [${FIRM.email}](mailto:${FIRM.email}).`,
  },

  privacy: {
    title: "Privacy Notice",
    description:
      "How AMADI Legal Consultancy collects, uses, and protects your personal data.",
    updated: UPDATED,
    body: `## 1. Introduction

${FIRM.entity} ("we", "us") is committed to protecting your privacy. This Notice explains how we collect, use, and safeguard personal data when you use our Website or contact us.

## 2. Information we collect

- **Information you provide** — such as your name, email address, company, and the content of any enquiry you submit.
- **Information collected automatically** — such as basic technical and usage data (for example, pages viewed) gathered through analytics.

## 3. How we use your information

- To respond to your enquiries and provide our services.
- To operate, maintain, and improve the Website.
- To comply with legal and regulatory obligations.

## 4. Legal bases for processing

We process personal data where it is necessary to respond to your request, where we have a legitimate interest in operating our practice, or where required to meet a legal obligation.

## 5. Sharing your information

We do not sell your personal data. We may share it with trusted service providers who support the operation of our Website and communications, subject to appropriate safeguards, or where required by law.

## 6. Data retention

We retain personal data only for as long as necessary for the purposes described in this Notice, or as required by applicable law.

## 7. Your rights

Subject to applicable law, you may have the right to access, correct, or request deletion of your personal data, and to object to or restrict certain processing. To exercise these rights, contact us using the details below.

## 8. Security

We implement reasonable technical and organisational measures designed to protect personal data against unauthorised access, loss, or misuse.

## 9. International transfers

Where personal data is transferred across borders, we take steps to ensure it remains protected in accordance with applicable law.

## 10. Contact

For any privacy matters, please contact us at [${FIRM.email}](mailto:${FIRM.email}).`,
  },

  cookies: {
    title: "Cookies Notice",
    description:
      "How AMADI Legal Consultancy uses cookies and similar technologies.",
    updated: UPDATED,
    body: `## 1. What are cookies?

Cookies are small text files placed on your device when you visit a website. They help the site function and provide information about how it is used.

## 2. Cookies we use

- **Essential cookies** — required for the Website to operate correctly.
- **Analytics cookies** — help us understand how visitors interact with the Website so we can improve it. These may be set by trusted analytics providers.

## 3. Managing cookies

Most browsers allow you to control cookies through their settings, including blocking or deleting them. Please note that disabling certain cookies may affect how the Website functions.

## 4. Changes to this notice

We may update this Cookies Notice periodically to reflect changes in the technologies we use.

## 5. Contact

If you have questions about our use of cookies, contact us at [${FIRM.email}](mailto:${FIRM.email}).`,
  },

  "legal-notices": {
    title: "Legal Notices",
    description:
      "Important legal information regarding AMADI Legal Consultancy and this website.",
    updated: UPDATED,
    body: `## 1. Firm information

This Website is operated by ${FIRM.entity}. Details of the firm's registration and regulatory status are available on request.

## 2. No legal advice

Information on this Website is general in nature and does not constitute legal advice or an offer to provide legal services. No lawyer–client relationship is formed by accessing this Website or by contacting us through it.

## 3. Accuracy of information

While we aim to keep the information on this Website current and accurate, we make no warranty as to its completeness or accuracy and accept no responsibility for reliance placed on it.

## 4. Limitation of liability

To the fullest extent permitted by law, ${FIRM.entity} excludes liability for any loss or damage arising from the use of, or inability to use, this Website.

## 5. Governing law and jurisdiction

These notices and any matter arising from your use of the Website are governed by the laws of ${FIRM.governingLaw}.

## 6. Contact

For legal enquiries relating to this Website, contact [${FIRM.email}](mailto:${FIRM.email}).`,
  },

  "fraud-alert": {
    title: "Fraud Alert",
    description:
      "Guidance on identifying and reporting fraudulent communications impersonating AMADI.",
    updated: UPDATED,
    body: `## Protect yourself from fraud

We are aware that individuals and organisations occasionally impersonate reputable firms — including through fake emails, messages, websites, and social media accounts — to defraud the public. Please read the guidance below.

## What ${FIRM.name} will never do

- Ask you to transfer funds to a personal account or an unverified account.
- Request sensitive personal or financial information through unsolicited messages.
- Guarantee visas, jobs, grants, prizes, or investment returns.
- Demand urgent payment of "fees", "taxes", or "charges" to release funds.

## How to verify a communication

- Check that any email originates from an official ${FIRM.name} domain.
- Be cautious of poor grammar, mismatched sender addresses, and pressure to act urgently.
- When in doubt, contact us directly using the verified details on this Website before taking any action.

## Reporting suspected fraud

If you receive a communication you believe fraudulently uses our name, do not respond, click links, or make any payment. Please report it to us at [${FIRM.email}](mailto:${FIRM.email}) and, where appropriate, to your local authorities.

## Disclaimer

${FIRM.entity} accepts no liability for any loss resulting from fraudulent communications carried out by third parties impersonating the firm.`,
  },

  trademark: {
    title: "Trademark Policy",
    description:
      "Guidelines on the use of AMADI Legal Consultancy's trademarks and brand assets.",
    updated: UPDATED,
    body: `## 1. Our trademarks

The ${FIRM.name} name, logo, and associated brand assets (the "Marks") are trademarks of ${FIRM.entity} and are protected by applicable law.

## 2. Permitted use

You may refer to our name for accurate, descriptive, and non-misleading purposes — for example, in referencing our services or publications — provided such use does not imply endorsement or affiliation without our consent.

## 3. Prohibited use

You may not:

- Use the Marks in a way that is misleading or suggests a false association with ${FIRM.entity}.
- Alter, distort, or incorporate the Marks into your own logos, products, or materials.
- Use the Marks in connection with unlawful, fraudulent, or disparaging activities.

## 4. Reporting misuse

If you become aware of unauthorised or improper use of our Marks, please notify us at [${FIRM.email}](mailto:${FIRM.email}).

## 5. Reservation of rights

All rights not expressly granted are reserved by ${FIRM.entity}.`,
  },
};
